// CR038: Hook for managing community candidate scoring configuration

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { 
  CommunityCandidateScoringConfig, 
  CommunitySourceScope,
  CommunitySignal 
} from "@/lib/community-signals/types";

export function useCommunityCandidateConfig(scope: CommunitySourceScope = 'ALL') {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch the active config for the selected scope
  const {
    data: config,
    isLoading,
    refetch,
    error
  } = useQuery({
    queryKey: ["community-candidate-config", scope],
    queryFn: async (): Promise<CommunityCandidateScoringConfig | null> => {
      const { data, error } = await supabase
        .from("community_candidate_scoring_config")
        .select("*")
        .eq("scope", scope)
        .eq("is_active", true)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) return null;

      return {
        ...data,
        signals: (data.signals as unknown as CommunitySignal[]) || [],
        threshold: Number(data.threshold),
      } as CommunityCandidateScoringConfig;
    }
  });

  // Fetch all configs (for scope dropdown)
  const {
    data: allConfigs,
    isLoading: isLoadingAllConfigs
  } = useQuery({
    queryKey: ["community-candidate-configs-all"],
    queryFn: async (): Promise<CommunityCandidateScoringConfig[]> => {
      const { data, error } = await supabase
        .from("community_candidate_scoring_config")
        .select("*")
        .eq("is_active", true)
        .order("scope", { ascending: true });

      if (error) throw error;
      
      return (data || []).map(d => ({
        ...d,
        signals: (d.signals as unknown as CommunitySignal[]) || [],
        threshold: Number(d.threshold),
      })) as CommunityCandidateScoringConfig[];
    }
  });

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: async (updates: Partial<CommunityCandidateScoringConfig>) => {
      if (!config?.id) throw new Error("No config to update");

      const { error } = await supabase
        .from("community_candidate_scoring_config")
        .update({
          threshold: updates.threshold,
          signals: JSON.parse(JSON.stringify(updates.signals || [])),
          enabled: updates.enabled,
          version: `v${Date.now()}`,
        })
        .eq("id", config.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Config saved", description: "Candidate scoring config updated." });
      queryClient.invalidateQueries({ queryKey: ["community-candidate-config"] });
      queryClient.invalidateQueries({ queryKey: ["community-candidate-configs-all"] });
    },
    onError: (error) => {
      toast({ 
        title: "Save failed", 
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive" 
      });
    }
  });

  // Update signals only
  const updateSignals = (signals: CommunitySignal[]) => {
    updateConfigMutation.mutate({ signals });
  };

  // Update threshold
  const updateThreshold = (threshold: number) => {
    updateConfigMutation.mutate({ threshold });
  };

  // Toggle enabled
  const toggleEnabled = (enabled: boolean) => {
    updateConfigMutation.mutate({ enabled });
  };

  return {
    config,
    allConfigs,
    isLoading: isLoading || isLoadingAllConfigs,
    error,
    updateSignals,
    updateThreshold,
    toggleEnabled,
    isSaving: updateConfigMutation.isPending,
    refetch,
  };
}
